import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabaseClient';
import { normalizeService } from '../../utils/bookingUtils';
import { useTranslation } from 'react-i18next';

// Define types
export type Service = {
  id: string;
  name: string;
  duration: number | string;
  price: number | string;
  category_id: string;
  short_description?: string;
  description?: string;
  numericPrice?: number;
  numericDuration?: number;
  discountedPrice?: string;
  is_active?: boolean;
};

export type ServiceCategory = {
  id: string;
  title?: string;
  name?: string;
  description: string;
  image?: string;
  image_url?: string;
  services: Service[];
};

type ServiceContextType = {
  categories: ServiceCategory[];
  services: Service[];
  isLoading: boolean;
  error: string | null;
  refreshData: (forceRefresh?: boolean) => Promise<void>;
  getCategoryById: (id: string) => ServiceCategory | undefined;
  getServiceById: (id: string) => Service | undefined;
};

const ServiceContext = createContext<ServiceContextType>({
  categories: [],
  services: [],
  isLoading: true,
  error: null,
  refreshData: async () => {},
  getCategoryById: () => undefined,
  getServiceById: () => undefined,
});

const CACHE_STORAGE_KEY = 'service_data_cache';
const CACHE_EXPIRY_MS = 10 * 60 * 1000; // 10 minutes

// Custom display order for service categories (by name/title)
const CATEGORY_DISPLAY_ORDER = [
  'LA MER COLLECTION',
  'Valmont Series',
  'Skinbolic Premium Series',
  'Traditional Meridian Sculpting (BoJing) Therapy',
  'Wellness & Body Therapies',
];

export const ServiceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { t, i18n } = useTranslation(['serviceContent']);

  // Use refs to avoid dependency issues and prevent concurrent fetches
  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const categoriesRef = useRef<ServiceCategory[]>([]);
  const initialLoadDoneRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    categoriesRef.current = categories;
  }, [categories]);

  // Fetch service data from Supabase
  const fetchServiceData = useCallback(async (forceRefresh = false) => {
    // Prevent concurrent fetches
    if (isFetchingRef.current) {
      console.log('[ServiceContext] Already fetching, skipping...');
      return;
    }

    const now = Date.now();
    const cacheExpired = (now - lastFetchTimeRef.current) > CACHE_EXPIRY_MS;

    // Return cached data if cache hasn't expired and we're not forcing a refresh
    if (!forceRefresh && !cacheExpired && categoriesRef.current.length > 0) {
      return;
    }

    // If forcing refresh, clear localStorage cache first
    if (forceRefresh) {
      try {
        localStorage.removeItem(CACHE_STORAGE_KEY);
      } catch (e) {
        console.warn('Failed to clear service cache from localStorage:', e);
      }
    }

    isFetchingRef.current = true;

    try {
      setIsLoading(true);
      setError(null);
      
      // Fetch service categories
      console.log('[ServiceContext] Fetching from Supabase...');
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('service_categories')
        .select('*')
        .order('created_at');

      console.log('[ServiceContext] Supabase response:', { categoriesData, categoriesError });

      if (categoriesError) {
        throw new Error(`Error fetching categories: ${categoriesError.message}`);
      }
      
      if (!categoriesData || categoriesData.length === 0) {
        setCategories([]);
        setServices([]);
        setIsLoading(false);
        return;
      }
      
      // Get current language
      const currentLanguage = i18n.language;
      
      // For each category, fetch its services
      const categoriesWithServices = await Promise.all(
        categoriesData.map(async (category: any) => {
          const { data: servicesData, error: servicesError } = await supabase
            .from('services')
            .select('*')
            .eq('category_id', category.id)
            .order('created_at');
          
          if (servicesError) {
            console.error(`Error fetching services for category ${category.id}:`, servicesError);
            
            // Apply language-aware category data
            const categoryTitle = currentLanguage === 'es' && category.title_es
              ? category.title_es
              : currentLanguage === 'es'
                ? t(`serviceContent:categories.${category.id}.title`, category.title || category.name || '')
                : (category.title || category.name || '');
            
            const categoryDescription = currentLanguage === 'es' && category.description_es
              ? category.description_es
              : currentLanguage === 'es'
                ? t(`serviceContent:categories.${category.id}.description`, category.description || '')
                : category.description || '';
            
            return {
              ...category,
              title: categoryTitle,
              description: categoryDescription,
              services: []
            };
          }
          
          // Format the services to ensure price is properly formatted and add numericPrice/numericDuration
          const formattedServices = servicesData?.map(service => {
            // Language-aware service name
            // Priority: 1) DB Spanish field, 2) JSON translation, 3) English from DB
            const serviceName = currentLanguage === 'es' && service.name_es
              ? service.name_es
              : currentLanguage === 'es'
                ? t(`serviceContent:services.${service.id}.name`, service.name || '')
                : service.name || '';
            
            // Language-aware service description
            const serviceDescription = currentLanguage === 'es' && service.description_es
              ? service.description_es
              : currentLanguage === 'es'
                ? t(`serviceContent:services.${service.id}.description`, service.description || '')
                : service.description || '';
            
            // Make sure price is a valid number for storing internally
            let numericPrice = 0;
            let displayPrice = "$0.00";
            
            if (service.price) {
              try {
                if (typeof service.price === 'number') {
                  numericPrice = service.price;
                } else {
                  // Try to parse as a number, removing any non-numeric chars except decimal point
                  const cleanedPrice = service.price.toString().replace(/[^0-9.]/g, '');
                  numericPrice = parseFloat(cleanedPrice);
                }
                
                // Set display price with dollar sign if we have a valid number
                if (!isNaN(numericPrice)) {
                  displayPrice = `${numericPrice.toFixed(2)}`;
                } else {
                  console.warn(`Invalid price for service ${service.id}: ${service.price}`);
                  numericPrice = 0;
                  displayPrice = "$0.00";
                }
              } catch (e) {
                console.error(`Error formatting price for service ${service.id}:`, e);
              }
            }
            
            // Format duration as string with "minutes"
            let displayDuration;
            if (typeof service.duration === 'number') {
              displayDuration = `${service.duration} minutes`;
            } else if (typeof service.duration === 'string' && service.duration.includes('minute')) {
              displayDuration = service.duration;
            } else {
              displayDuration = `${parseInt(service.duration || '0') || 0} minutes`;
            }
            
            return {
              ...service,
              name: serviceName,
              description: serviceDescription,
              price: displayPrice,
              duration: displayDuration,
              // Store numeric values in additional properties for booking
              numericPrice,
              numericDuration: parseInt(service.duration || '0') || 0
            };
          }) || [];
          
          // Apply language-aware category data
          const categoryTitle = currentLanguage === 'es' && category.title_es
            ? category.title_es
            : currentLanguage === 'es'
              ? t(`serviceContent:categories.${category.id}.title`, category.title || category.name || '')
              : (category.title || category.name || '');
          
          const categoryDescription = currentLanguage === 'es' && category.description_es
            ? category.description_es
            : currentLanguage === 'es'
              ? t(`serviceContent:categories.${category.id}.description`, category.description || '')
              : category.description || '';
          
          return {
            ...category,
            title: categoryTitle,
            description: categoryDescription,
            services: [...formattedServices]
              .filter(service => service.is_active !== false)
              .sort((a, b) => (a.numericPrice || 0) - (b.numericPrice || 0))
          };
        })
      );
      
      // Sort categories by custom display order
      const sortedCategories = [...categoriesWithServices].sort((a, b) => {
        const aName = a.title || a.name || '';
        const bName = b.title || b.name || '';
        const aIndex = CATEGORY_DISPLAY_ORDER.findIndex(name =>
          aName.toLowerCase().includes(name.toLowerCase()) || name.toLowerCase().includes(aName.toLowerCase())
        );
        const bIndex = CATEGORY_DISPLAY_ORDER.findIndex(name =>
          bName.toLowerCase().includes(name.toLowerCase()) || name.toLowerCase().includes(bName.toLowerCase())
        );
        // If both found in order list, sort by their position
        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        // If only one found, it comes first
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        // If neither found, maintain original order
        return 0;
      });

      // Filter out categories with no visible services
      const visibleCategories = sortedCategories.filter(
        (category: ServiceCategory) => category.services.length > 0
      );

      // Flatten all services into a single array for easy lookups
      const allServices = visibleCategories.flatMap((category: ServiceCategory) => category.services);

      setCategories(visibleCategories);
      setServices(allServices);
      lastFetchTimeRef.current = now;
      
      // Cache the data in localStorage
      try {
        localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify({
          categories: sortedCategories,
          timestamp: now
        }));
      } catch (e) {
        console.warn('Failed to cache service data to localStorage:', e);
      }
    } catch (err: any) {
      console.error('Error fetching service data:', err);
      setError(err.message || 'An error occurred while fetching services');
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, [t, i18n.language]); // Only depend on translation function and language
  
  // Load initial data from cache or fetch fresh data (runs once on mount)
  useEffect(() => {
    if (initialLoadDoneRef.current) return;
    initialLoadDoneRef.current = true;

    const loadInitialData = async () => {
      try {
        // Try to load from localStorage first for immediate display
        const cachedData = localStorage.getItem(CACHE_STORAGE_KEY);
        if (cachedData) {
          const { categories: cachedCategories, timestamp } = JSON.parse(cachedData);
          const cacheAge = Date.now() - timestamp;

          if (cacheAge < CACHE_EXPIRY_MS && cachedCategories?.length > 0) {
            setCategories(cachedCategories);
            setServices(cachedCategories.flatMap((category: ServiceCategory) => category.services));
            setIsLoading(false);
            lastFetchTimeRef.current = timestamp;
            return;
          }
        }

        // No valid cache, do a normal fetch
        await fetchServiceData(true);
      } catch (e) {
        console.error('Error initializing service data:', e);
        await fetchServiceData(true);
      }
    };

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - run once on mount

  // Re-fetch data when language changes (but not on initial load)
  const prevLanguageRef = useRef(i18n.language);
  useEffect(() => {
    // Skip if language hasn't actually changed
    if (prevLanguageRef.current === i18n.language) return;
    prevLanguageRef.current = i18n.language;

    // Only refetch if we have data already
    if (categoriesRef.current.length > 0) {
      fetchServiceData(true);
    }
  }, [i18n.language, fetchServiceData]);
  
  // Helper functions
  const getCategoryById = (id: string) => {
    return categories.find(category => category.id === id);
  };
  
  const getServiceById = (id: string) => {
    return services.find(service => service.id === id);
  };
  
  // Context value
  const contextValue: ServiceContextType = {
    categories,
    services,
    isLoading,
    error,
    refreshData: fetchServiceData,
    getCategoryById,
    getServiceById,
  };
  
  return (
    <ServiceContext.Provider value={contextValue}>
      {children}
    </ServiceContext.Provider>
  );
};

// Custom hook for using the service context
export const useServices = () => useContext(ServiceContext);

export default ServiceContext;